import { BusinessObjectBase } from './base';
import { processDesignHistorySchema } from '@schemas/business-objects/process-design-history';
import type { ProcessDesign } from './process-design';
import type { Tenant } from './tenant';
import type {
  HistoryData,
  Id,
  ProcessDesignHistoryEventType,
} from '../base-types';

export interface ProcessDesignHistoryData {
  readonly id: Id;
  readonly eventType: ProcessDesignHistoryEventType;
  readonly data?: HistoryData;
  readonly timestamp?: Date;
  readonly processDesignId: Id;
  readonly processDesign?: ProcessDesign;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface ProcessDesignHistory extends Readonly<ProcessDesignHistoryData> {}
type ProcessDesignHistoryCreateWithChildrenData =
  Partial<ProcessDesignHistoryData>;

export class ProcessDesignHistory extends BusinessObjectBase {
  static readonly entityName = 'ProcessDesignHistory' as const;
  static readonly plural = 'process-design-histories';
  static schema = processDesignHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDesignHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDesignHistory(data));
  }

  static async findOne(filter?: any): Promise<ProcessDesignHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDesignHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessDesignHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDesignHistory(data);
  }

  static async create(
    data: Omit<ProcessDesignHistoryData, 'id'>,
  ): Promise<ProcessDesignHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDesignHistory(result);
  }

  static async createMany(
    data: Omit<ProcessDesignHistoryData, 'id'>[],
  ): Promise<ProcessDesignHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesignHistory(item));
  }

  static async createWithChildren(
    data: ProcessDesignHistoryCreateWithChildrenData,
  ): Promise<ProcessDesignHistory> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDesignHistory(result);
  }

  static async createManyWithChildren(
    data: ProcessDesignHistoryCreateWithChildrenData[],
  ): Promise<ProcessDesignHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesignHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessDesignHistoryData>,
  ): Promise<ProcessDesignHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDesignHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
